/**
 * Voice dictation via Deepgram streaming WebSocket.
 *
 * Flow:
 *  1. User clicks mic → we getUserMedia (microphone)
 *  2. Create AudioContext + ScriptProcessor (or AudioWorklet) that downsamples to 16kHz mono PCM
 *  3. Open WebSocket to wss://api.deepgram.com/v1/listen?... with the user's API key
 *  4. Stream audio frames as binary; server returns JSON with Results.channel.alternatives[0].transcript
 *  5. On `is_final: true`, commit transcript via onTranscript(text, true)
 *     On interim results, call onTranscript(text, false) so UI can preview
 *  6. User clicks mic again → stop
 *
 * Security note: the API key lives in the browser during the session. That's an
 * accepted tradeoff for an on-prem clinic app; upgrade later by having the
 * paired image service mint Deepgram temp-tokens if key exposure becomes a concern.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  loadUserSettings, loadSettingsFull,
  DEFAULT_DEEPGRAM_HOST, DEFAULT_DEEPGRAM_MODEL, DEFAULT_DEEPGRAM_ENDPOINTING,
  DEFAULT_POSTPROCESS_ENDPOINT, DEFAULT_POSTPROCESS_MODEL,
} from '../utils/userSettings';
import {
  DictationReplacement, DEFAULT_CLIENT_REPLACEMENTS,
  applyReplacements, deepgramReplaceParams, loadReplacements,
  mergeUniversals,
} from '../utils/dictationReplacements';
import { postProcessTranscript, type PostProcessConfig } from '../utils/dictationPostprocess';

export interface DictateButtonHandle {
  /** Programmatically toggle dictation on/off (used by the F4 shortcut). */
  toggle: () => void;
  isListening: () => boolean;
}

interface Props {
  /** Called with (text, isFinal). Interim updates may be called many times; final only once per utterance. */
  onTranscript: (text: string, isFinal: boolean) => void;
  disabled?: boolean;
}

const DG_SAMPLE_RATE = 16000;

const DictateButton = forwardRef<DictateButtonHandle, Props>(function DictateButton(
  { onTranscript, disabled }: Props,
  ref
) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  // User's client-side voice-replacement dictionary. Reloaded every time
  // dictation starts so a Settings edit takes effect on the next click
  // without waiting for a page reload.
  const replacementsRef = useRef<DictationReplacement[]>([]);
  // Snapshot of the user's post-process config, captured once at session
  // start so toggling the Settings in another tab mid-session doesn't
  // split transcripts between cleaned + un-cleaned.
  const postProcessRef = useRef<PostProcessConfig>({
    enabled: false, endpoint: '', model: '', apiKey: '',
  });

  useEffect(() => () => { stop(); }, []);

  async function start() {
    setError('');
    const settings = await loadUserSettings();
    const key = settings.deepgramKey?.trim();
    if (!key) {
      setError('No Deepgram key configured. Open Settings to add one.');
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // Browsers only expose mediaDevices on secure contexts (HTTPS or localhost).
      // On plain HTTP over LAN (common on-prem), mic access is blocked.
      setError(
        'Mic unavailable: the browser only exposes microphone access on HTTPS or localhost. ' +
        'Serve the app over HTTPS, or add this origin to chrome://flags/#unsafely-treat-insecure-origin-as-secure for testing.'
      );
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true },
      });
    } catch (err: any) {
      setError('Mic access denied: ' + (err?.message || 'unknown'));
      return;
    }
    streamRef.current = stream;

    // Refresh the client-side replacement dictionary each time we start
    // dictation so a Settings edit takes effect on the next mic click
    // without needing a page reload. force=true bypasses the module-level
    // settings cache, which is critical when the report editor lives in
    // a separate window/tab from the one where Settings was saved: that
    // tab's cache isn't invalidated by the other tab's saveUserSettings.
    // Falling back to DEFAULT_CLIENT_REPLACEMENTS gives first-time users
    // working punctuation before they've saved any settings; user-saved
    // lists (even empty ones) take precedence.
    try {
      await loadSettingsFull(true);
      const r = await loadReplacements();
      const base = r.replacements.length > 0
        ? r.replacements
        : [...DEFAULT_CLIENT_REPLACEMENTS];
      // ALWAYS merge in UNIVERSAL_PUNCTUATION as a fallback. User entries
      // take precedence on collision, so this doesn't override anything
      // the user explicitly customised - it just fills in anything they
      // deleted or never had. This makes punctuation reliable even when
      // Deepgram's server-side dictation flag misbehaves.
      replacementsRef.current = mergeUniversals(base);
      // Flat string so DevTools copy-paste captures the data (console.info
      // with an object argument shows "Object" in copied output).
      const sample = replacementsRef.current
        .slice(0, 10)
        .map((e) => `${e.spoken}->${JSON.stringify(e.replacement)}`)
        .join(', ');
      console.log(
        `[Dictate] dictionary loaded userEntries=${r.replacements.length} total=${replacementsRef.current.length} sample=${sample}`,
      );
    } catch (err) {
      console.warn('[Dictate] dictionary load failed, using defaults + universals', err);
      replacementsRef.current = mergeUniversals([...DEFAULT_CLIENT_REPLACEMENTS]);
    }

    // Snapshot post-process config for this session. OFF by default; the
    // user opts in via Settings > Dictation. Endpoint + model have sane
    // defaults (Groq's free Llama 3.3 70B) so a user who just pastes
    // their Groq key + flips the toggle gets a working setup.
    postProcessRef.current = {
      enabled: !!settings.dictationPostProcessEnabled && !!settings.dictationPostProcessKey?.trim(),
      endpoint: settings.dictationPostProcessEndpoint?.trim() || DEFAULT_POSTPROCESS_ENDPOINT,
      model: settings.dictationPostProcessModel?.trim() || DEFAULT_POSTPROCESS_MODEL,
      apiKey: settings.dictationPostProcessKey?.trim() || '',
    };
    console.log(
      `[Dictate] post-process ${postProcessRef.current.enabled ? 'ON' : 'off'} (${postProcessRef.current.model})`,
    );

    // Deepgram streaming endpoint. Host, model, and endpointing are all
    // read from user settings so a clinic can tune for its region and
    // dictation style without a code change. Defaults in userSettings.ts.
    //   - host: regional POP (api.in.deepgram.com for India by default)
    //   - model: nova-3-medical for clinical vocabulary
    //   - endpointing: 300ms balances snappy finals vs. not cutting
    //     off natural mid-sentence pauses
    const host = settings.deepgramHost?.trim() || DEFAULT_DEEPGRAM_HOST;
    const model = settings.deepgramModel?.trim() || DEFAULT_DEEPGRAM_MODEL;
    const endpointingMs = settings.deepgramEndpointing ?? DEFAULT_DEEPGRAM_ENDPOINTING;
    // Deepgram query params:
    //   smart_format=true: formats entities (numbers, dates, times) AND
    //     implies punctuate=true. Docs: "If you've set smart_format=true,
    //     no need to also set punctuate=true."
    //   dictation=true: converts spoken punctuation words to symbols
    //     ("comma" -> ",", "period" -> "." etc). This is the Deepgram
    //     feature we NEED — without it, "comma" is transcribed as the
    //     literal word. Requires punctuate (satisfied by smart_format).
    //   punctuate=true: kept explicit as belt-and-suspenders in case a
    //     future Deepgram version decouples smart_format from punctuate.
    //   interim_results=true: streams partial transcripts.
    const qp = new URLSearchParams({
      model,
      encoding: 'linear16',
      sample_rate: String(DG_SAMPLE_RATE),
      channels: '1',
      interim_results: 'true',
      smart_format: 'true',
      punctuate: 'true',
      dictation: 'true',
      endpointing: String(endpointingMs),
    });
    // Keyterm boost: tell Deepgram's Nova-3 ASR to prioritize recognizing
    // every spoken-form in the user's dictionary + the universal
    // punctuation set. Without this, nova-3-medical biases toward
    // clinical vocabulary and can hear "semicolon" as "simmy colon" or
    // drop it entirely; "full stop" (Indian English) similarly suffers.
    // Up to 100 keyterms per connection (we send well under 50).
    //
    // The keyterm boost just improves RECOGNITION of these words as
    // literal tokens — it does NOT do the symbol conversion. That's
    // handled by:
    //   - dictation=true server-side (for comma, period, colon, ? and !)
    //   - client-side applyReplacements with UNIVERSAL_PUNCTUATION +
    //     user's custom entries (for everything else, including
    //     "full stop" and "semicolon")
    const keyterms = new Set<string>();
    for (const e of replacementsRef.current) {
      const s = (e.spoken || '').trim();
      // Drop empty, overly-long, and single-char entries. Deepgram
      // doesn't boost usefully for single chars; long phrases are
      // better handled by `keywords` in older models but keyterm
      // takes short phrases fine.
      if (s && s.length >= 2 && s.length <= 40) keyterms.add(s);
    }
    for (const term of keyterms) qp.append('keyterm', term);
    console.log(`[Dictate] boosting ${keyterms.size} keyterms: ${[...keyterms].join(', ')}`);
    // Push the user's OWN dictionary (single-line entries only) to Deepgram
    // as `replace=` params so live interim transcripts already show the
    // substitutions. Multi-line entries (like "new paragraph") only get
    // applied client-side on the FINAL transcript. This keeps the user's
    // list the single source of truth: removing "comma" here means Deepgram
    // stops substituting it too.
    for (const [k, v] of deepgramReplaceParams(replacementsRef.current)) {
      qp.append(k, v);
    }
    const wsUrl = `wss://${host}/v1/listen?${qp}`;
    console.info('[Dictate] connecting to Deepgram', { host, model, endpointingMs });
    const ws = new WebSocket(wsUrl, ['token', key]);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    // Diagnostic: warn the user if we've been "Listening" for a while but
    // no transcript has come back. Almost always means Deepgram rejected
    // the audio silently (bad key, unsupported model, mic muted, or the
    // browser is holding a stale mic capture from a previous session).
    let sawFirstTranscript = false;
    const silenceWatchdog = setTimeout(() => {
      if (!sawFirstTranscript && wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
        setError('No transcript after 10s. Check mic input, Deepgram key, or model access.');
        console.warn('[Dictate] no transcript within 10s; connection is open but silent');
      }
    }, 10_000);

    ws.onopen = () => {
      console.info('[Dictate] WebSocket open, starting audio capture');
      // AudioContext captures mic, processor downsamples to 16kHz mono PCM, we send over ws
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;

      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      const inRate = ctx.sampleRate;
      let framesSent = 0;
      processor.onaudioprocess = (evt) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const inBuf = evt.inputBuffer.getChannelData(0);
        const out = downsampleTo16kMono(inBuf, inRate, DG_SAMPLE_RATE);
        if (out.length > 0) {
          ws.send(out.buffer);
          if (framesSent === 0) console.info('[Dictate] first audio frame sent, size', out.length);
          framesSent++;
        }
      };

      source.connect(processor);
      processor.connect(ctx.destination);
      setListening(true);
    };

    ws.onmessage = (msg) => {
      try {
        const data = JSON.parse(typeof msg.data === 'string' ? msg.data : '');
        // Deepgram sends periodic Metadata frames (channel_index, model info)
        // that don't have alternatives. Also error frames like {type:'Error', ...}.
        if (data?.type === 'Error') {
          console.error('[Dictate] Deepgram error frame', data);
          setError(`Deepgram: ${data.description || data.message || 'unknown error'}`);
          return;
        }
        const alt = data?.channel?.alternatives?.[0];
        if (!alt) return;
        // Strip only spaces + tabs; preserve newlines. Deepgram's
        // smart_format emits "\n" / "\n\n" for spoken "new line" and
        // "new paragraph". A plain .trim() ate those for standalone
        // utterances (text became "") and the paragraph break was lost.
        const raw = alt.transcript || '';
        const txt = raw.replace(/^[ \t]+|[ \t]+$/g, '');
        // Keep pure-whitespace transcripts if they contain newlines -
        // those ARE Deepgram's paragraph commands.
        if (!txt && !raw.includes('\n')) return;
        if (!sawFirstTranscript) {
          sawFirstTranscript = true;
          console.info('[Dictate] first transcript received');
        }
        const isFinal = !!data.is_final;
        // Apply the user's dictionary to BOTH interim and final transcripts.
        // Previously interim passed through untouched for perf, but
        // applyReplacements is a few regex passes on short strings - fast
        // enough that running it 10x/sec is invisible to the user. Doing
        // it on interim means custom shortcuts (NAD, mnemonics, etc.) are
        // visible as the user speaks, not only after the final commits.
        const replaced = applyReplacements(
          txt || raw,  // if whitespace-only (standalone paragraph cmd), use raw
          replacementsRef.current,
        );
        // Flat-string log so copy-paste from DevTools shows the data
        // directly rather than "Object" placeholders. Also JSON-encodes
        // special chars (newlines etc.) so we can see what Deepgram
        // actually sent, including whitespace-only transcripts.
        try {
          console.log(
            `[Dictate] ${isFinal ? 'FINAL  ' : 'interim'} raw=${JSON.stringify(raw)} replaced=${JSON.stringify(replaced)}`,
          );
        } catch { /* ignore */ }
        // Interim: no post-process (burns free-tier RPM for text the user
        // doesn't see anyway now that the editor only commits on final).
        if (!isFinal) {
          onTranscript(replaced, isFinal);
          return;
        }
        // Final: optional LLM pass. If disabled or it fails, we still
        // emit the dictionary-replaced text so dictation never stops
        // working.
        if (postProcessRef.current.enabled) {
          void postProcessTranscript(replaced, postProcessRef.current).then((cleaned) => {
            onTranscript(cleaned, true);
          });
        } else {
          onTranscript(replaced, true);
        }
      } catch { /* ignore non-JSON frames */ }
    };

    ws.onerror = (e) => {
      console.error('[Dictate] WebSocket error', e);
      setError('Deepgram connection error - check key & network');
      stop();
    };
    ws.onclose = (e) => {
      clearTimeout(silenceWatchdog);
      console.info('[Dictate] WebSocket closed', { code: e.code, reason: e.reason });
      setListening(false);
      // Deepgram common close codes: 1000 clean, 1006 abnormal (network),
      // 1008 policy (bad key / unauth), 1011 server error, 4008 quota.
      if (e.code !== 1000 && !error) {
        const hint = e.code === 1008 ? ' - bad API key?'
                    : e.code === 4008 ? ' - Deepgram quota exceeded'
                    : e.reason ? ` - ${e.reason}`
                    : '';
        setError(`Connection closed (${e.code})${hint}`);
      }
    };
  }

  function stop() {
    try { processorRef.current?.disconnect(); } catch {}
    try { sourceRef.current?.disconnect(); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify({ type: 'CloseStream' })); } catch {}
      try { wsRef.current.close(); } catch {}
    }
    processorRef.current = null;
    sourceRef.current = null;
    audioCtxRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
    setListening(false);
  }

  function toggle() {
    if (listening) stop(); else void start();
  }

  useImperativeHandle(ref, () => ({
    toggle,
    isListening: () => listening,
  }), [listening]);

  return (
    <>
      <button
        onClick={toggle}
        disabled={disabled}
        title={listening ? 'Stop dictation' : 'Start dictation'}
        style={{
          padding: '4px 10px',
          border: 'none',
          borderRadius: 3,
          fontSize: 12,
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: listening ? '#c53030' : '#2b6cb0',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
        {listening ? 'Listening…' : 'Dictate'}
      </button>
      {error && (
        <span style={{ fontSize: 10, color: '#fed7d7', padding: '0 6px' }}>{error}</span>
      )}
    </>
  );
});

export default DictateButton;

/** Downsample a Float32 array from inRate to outRate (16kHz), return Int16Array (PCM16 little-endian). */
function downsampleTo16kMono(input: Float32Array, inRate: number, outRate: number): Int16Array {
  if (inRate === outRate) return floatToPCM16(input);
  const ratio = inRate / outRate;
  const newLen = Math.round(input.length / ratio);
  const out = new Float32Array(newLen);
  let offsetIn = 0;
  for (let i = 0; i < newLen; i++) {
    const next = Math.min(Math.round((i + 1) * ratio), input.length);
    let sum = 0;
    let n = 0;
    for (let j = offsetIn; j < next; j++) { sum += input[j]; n++; }
    out[i] = n ? sum / n : 0;
    offsetIn = next;
  }
  return floatToPCM16(out);
}

function floatToPCM16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return out;
}
